import React from 'react';
import { Container, Typography, Box, Grid, Card, CardContent, useMediaQuery, alpha, GlobalStyles } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import FanWaitlistForm from '../components/FanWaitlistForm';
import MetaTagManager, { createFanWaitlistMetaData } from '@/components/MetaTagManager';

// Import assets
import GirlHeadphonesImage from '../assets/lsnto-outside.png';
import IphoneAppImage from '../assets/iphoneapp2.png';
import CarAppImage from '../assets/car-app.png';

const FanWaitlistPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <MetaTagManager data={createFanWaitlistMetaData()}>
      {/* Add global styles for font */}
      <GlobalStyles
        styles={{
          '@import': 'url("https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap")',
          'body, .MuiTypography-root, .MuiButton-root, .MuiInputBase-root': {
            fontFamily: '"Poppins", sans-serif !important'
          },
          'input, select, textarea, label, .MuiInputLabel-root, .MuiMenuItem-root, .MuiChip-label': {
            fontFamily: '"Poppins", sans-serif !important'
          }
        }}
      />

      <Box
        sx={{
          background: 'linear-gradient(135deg, #d10046 0%, #2a0541 100%)',
          minHeight: '100vh',
          color: '#fff',
          py: { xs: 4, md: 0 },
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {/* Background gradient overlay with subtle animation */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(ellipse at bottom right, rgba(209,0,70,0.3), transparent 70%)',
            animation: 'pulse 8s infinite ease-in-out',
            '@keyframes pulse': {
              '0%': { opacity: 0.7 },
              '50%': { opacity: 0.9 },
              '100%': { opacity: 0.7 },
            },
            zIndex: 0,
          }}
        />

        {/* Main content container */}
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, height: '100%', py: { md: 4 } }}>
          <Grid 
            container 
            spacing={4} 
            sx={{ 
              minHeight: { md: 'calc(100vh - 64px)' },
              alignItems: { xs: 'center', md: 'flex-start' },
              pt: { md: 6 }
            }}
          >
            {/* Left column - Updated marketing copy */}
            <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', mb: { xs: 4, md: 0 } }}>
              {/* Image block moved above heading */}
              <Box 
                component="div" 
                sx={{ 
                  position: 'relative',
                  width: '100%',
                  height: { xs: '300px', sm: '380px', md: '460px' },
                  borderRadius: '16px',
                  overflow: 'hidden',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
                  mb: 3,
                }}
              >
                <img 
                  src={GirlHeadphonesImage} 
                  alt="Music fan with headphones" 
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center bottom',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    padding: 3,
                    background: 'linear-gradient(to top, rgba(0,0,0,0.85), transparent)',
                    textAlign: 'center',
                    // Add fadeSlideIn keyframes here
                    '@keyframes fadeSlideIn': {
                      '0%': { opacity: 0, transform: 'translateY(20px)' },
                      '100%': { opacity: 1, transform: 'translateY(0)' }
                    },
                  }}
                >
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontWeight: 800,
                      fontSize: { xs: '2rem', sm: '2.4rem', md: '2.8rem' },
                      textTransform: 'uppercase',
                      lineHeight: 1.1,
                      letterSpacing: '0.5px',
                      // Add animation and initial opacity for fade/slide
                      animation: 'fadeSlideIn 1.5s ease-out forwards',
                      opacity: 0,
                      textShadow: '0 4px 6px rgba(0,0,0,0.6)',
                    }}
                  >
                    FOR THOSE WHO STILL FEEL IT, WELCOME TO THE OTHER SIDE OF MUSIC
                  </Typography>
                </Box>
              </Box>

              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  mb: 3,
                  lineHeight: 1.2,
                  letterSpacing: '-0.02em',
                  position: 'relative',
                  display: 'inline-block',
                  width: 'fit-content',
                }}
              >
                Music is Being Stripped for Parts
                <Box
                  component="span"
                  sx={{
                    position: 'absolute',
                    left: 0,
                    bottom: -6,
                    width: '100%',
                    height: '6px',
                    backgroundColor: '#E4FF1A',
                    borderRadius: '3px',
                  }}
                />
              </Typography>


              <Typography variant="body1" sx={{ mb: 3, fontWeight: 500, maxWidth: '90%' }}>
                It’s not your imagination! Streaming has killed the soul. Your favourite artists get pennies. You get spoon-fed noise. The problem isn’t streaming. It’s who owns it.
              </Typography>

              {/* Boxed "You're Not a Fan" section */}
              <Box
                sx={{
                  backgroundColor: alpha('#000', 0.4),
                  borderLeft: '4px solid #E4FF1A',
                  borderRadius: '8px',
                  p: 3,
                  mb: 3,
                  boxShadow: '0 2px 12px rgba(0,0,0,0.2)'
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  You're not a "User"....    YOU ARE A <Box component="span" sx={{ color: '#E4FF1A', display: 'inline' }}>FAN</Box>
                </Typography>
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  Are you someone who goes to live music gigs and festivals every year without fail?
                </Typography>
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  Do you cherish your playlists? Still have your sacred CD and record collection?
                </Typography>
                <Typography variant="body2">
                  Have you ever told a mate “You have to hear this”?
                </Typography>
                <Typography variant="body2" sx={{ mt: 2, fontWeight: 600 }}>
                  If the answer is yes to any of these, then you are a fan... and you belong here.
                </Typography>
              </Box>


              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>This Isn’t a Platform. It’s a Counter-Movement.</Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                No major-label puppets. No autoplay trash. No AI deciding what you like. Just real artists, real connection, and music that hits like it used to.
              </Typography>

              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Sign Up. Stand Up.</Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Add your name. That’s it. No credit card. No tricks. Just one loud, defiant message: “I want music to mean something again.”
              </Typography>

              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>This Isn’t for Everyone.</Typography>
              <Typography variant="body2" sx={{ mb: 3 }}>
                It’s for the ones who care. The ones who still feel it. The ones who listen.
              </Typography>

              <Typography variant="body1" sx={{ fontWeight: 700, mb: 4 }}>
                Join the waitlist. Be early. Be proud. Be loud.
              </Typography>

              {/* Android Auto App Mockup Image */}
              <Box sx={{ mb: 4, textAlign: 'center' }}>
                <img 
                  src={CarAppImage} 
                  alt="Lsnto Android Auto App Display" 
                  style={{ 
                    maxWidth: '90%', 
                    height: 'auto', 
                    borderRadius: '16px', 
                    boxShadow: '0 10px 30px rgba(0,0,0,0.4)' 
                  }} 
                />
              </Box>
              {/* Gradient divider after Car image */}
              <Box sx={{
                height: '2px',
                width: '100%',
                background: 'linear-gradient(to right, transparent, #E4FF1A, transparent)',
                my: 4
              }} />

              {/* Text + iPhone mockup beside each other */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, mb: 4 }}>
                <Typography variant="body2" sx={{ maxWidth: '90%', opacity: 0.8, fontStyle: 'italic' }}>
                  We’re building Lsnto for the ones who care about the future of music. The ones who remember what it felt like to find that song. The ones who read liner notes, buy merch, go to gigs, and tell everyone about that artist they discovered before they blew up. Lsnto is where music lovers and independent artists unite. No major label puppeteering. No manufactured playlists. Just pure connection, fair rewards, and music that actually moves you.
                </Typography>
                <Box sx={{ flexShrink: 0 }}>
                  <img 
                    src={IphoneAppImage} 
                    alt="Lsnto iPhone App Mockup" 
                    style={{ 
                      height: '100%', 
                      maxHeight: '200px',
                      width: 'auto', 
                      borderRadius: '12px', 
                      boxShadow: '0 6px 20px rgba(0,0,0,0.3)' 
                    }} 
                  />
                </Box>
              </Box>

              <Box sx={{ 
                mb: 4, 
                p: 3, 
                borderRadius: '12px', 
                background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                border: '1px solid rgba(255,255,255,0.2)',
                backdropFilter: 'blur(10px)',
                position: 'relative',
              }}>
                <Typography variant="h5" sx={{
                  fontWeight: 700,
                  mb: 2,
                  color: '#fff',
                  fontSize: { xs: '1.2rem', md: '1.4rem' }
                }}>
                  We're not a tech company in music.<br />
                  <Box component="span" sx={{ color: '#E4FF1A' }}>We're a music company fixing the tech.</Box>
                </Typography>
                <Typography variant="body1" sx={{
                  lineHeight: 1.6,
                  fontSize: '0.95rem',
                  color: 'rgba(255,255,255,0.9)'
                }}>
                  Lsnto is a music platform built by artists, not tech giants! It has been designed to put creators back in control. Our goal is simple but seemingly radical: we want to reintroduce value to digital music by combining fair pay, fan-powered discovery, and innovative release tools.
                </Typography>
              </Box>
            </Grid>
            
            {/* Right column - Form */}
            <Grid item xs={12} md={6}>
              <Card 
                sx={{
                  maxWidth: { md: '90%', lg: '85%' },
                  borderRadius: '16px',
                  backgroundColor: alpha('#000', 0.5),
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                  overflow: 'visible',
                  ml: { md: 'auto' },
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: -10,
                    left: -10,
                    right: -10,
                    bottom: -10,
                    background: 'linear-gradient(45deg, #d10046, transparent, #2a0541)',
                    borderRadius: '20px',
                    zIndex: -1,
                    opacity: 0.6,
                  }
                }}
              >
                <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                    <img 
                      src="/logo2.png"
                      alt="Lsnto Logo" 
                      style={{
                        height: '60px',
                        width: 'auto',
                      }} 
                    />
                  </Box>
                  
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, textAlign: 'center' }}>
                    Join our Fan Waitlist
                  </Typography>
                  
                  <Typography variant="body1" sx={{ mb: 4, opacity: 0.9, textAlign: 'center' }}>
                    Get early access to a platform that connects you directly with the artists you love.
                  </Typography>
                  
                  <Box 
                    sx={{ 
                      pr: 2,
                      // Scrollbar styling
                      '&::-webkit-scrollbar': {
                        width: '8px',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        borderRadius: '4px',
                        display: 'block',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(255, 255, 255, 0.3)',
                        borderRadius: '4px',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.5)',
                        },
                      },
                      // Add styles to customize form fields 
                      '.MuiTextField-root, .MuiAutocomplete-root': {
                        mb: 2.5,
                      },
                      '.MuiInputLabel-root': {
                        fontWeight: 500,
                      },
                      '.MuiInputBase-root': {
                        borderRadius: '8px',
                      },
                      '.MuiButton-root': {
                        borderRadius: '8px',
                        fontWeight: 600,
                        py: 1.5,
                        mt: 2,
                      }
                    }}
                  >
                    <FanWaitlistForm />
                  </Box>
                  
                  <Typography variant="body2" sx={{ mt: 3, opacity: 0.7, textAlign: 'center', fontSize: '0.75rem' }}>
                    By submitting this form, you agree to our{' '}
                    <a href="/privacy-policy" style={{ color: '#E4FF1A', textDecoration: 'underline' }}>
                      Privacy Policy
                    </a>
                    .
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </MetaTagManager>
  );
};

export default FanWaitlistPage; 